/**
 * `describeViewerError` — the ONE place a file-pane failure becomes words a
 * lawyer reads.
 *
 * Its own module rather than living in `viewers.tsx`, for a measured reason:
 * `viewers.tsx` lazy-imports the three heavy viewers (`pdf.js` worker ~1.4 MB,
 * docx-preview, react-markdown) precisely to keep them out of the main chunk.
 * A static import back from those viewers into `viewers.tsx` would form a cycle
 * AND pull that module's eager dependency graph into every lazy chunk — quietly
 * undoing the code-splitting the file exists to preserve.
 */

/**
 * Map a `useFileBytes` failure to something a LAWYER can read.
 *
 * ## ⚠ This used to print the server's raw error, and it leaked a container path
 *
 * Live on 2026-08-13 the concierge opened a file that did not exist and this
 * surface rendered, verbatim and in red:
 *
 *     ENOENT: no such file or directory, open '/data/org/cases/…/notas-internas-2019.md'
 *
 * Two defects in one line: an English dev error shown to the user, and an
 * INTERNAL ABSOLUTE SERVER PATH in the UI. Neither was new — the surface had
 * simply been unreachable, because `useFileOpenFromAgent` matched a field name
 * the wire never sends and so had never fired until `deddc74b8`. Reviving a dead
 * path promotes every latent defect downstream of it to a live one.
 *
 * ## 🚨 Branch on the CODE. Never on the message text.
 *
 * `GatewayRpcError.code` survives to here intact (`useFileBytes`' queryFn returns
 * the RPC promise unwrapped, so react-query's `error` IS that class). The backend
 * message is being sanitized separately by `lexruntime-changes`, so any surviving
 * substring match on backend prose is a silent break waiting to happen — it would
 * quietly fall through to the generic state for a file that is merely missing.
 *
 * The one substring match that STAYS is the preview-limit mapping: that path is
 * `INVALID_PARAMS` (a code shared by several causes), so the text is currently
 * the only discriminator. It is a known exception, not an oversight.
 *
 * @param path Case-RELATIVE path, for the not-found state. Never an absolute one.
 */
export function describeViewerError(
  t: (k: string, o?: Record<string, unknown>) => string,
  err: Error | null,
  path?: string,
): string {
  if (!err) return t("workspace.filePane.viewer.errorGeneric");
  const code = (err as { code?: string }).code;
  if (code === "NOT_FOUND") {
    return path
      ? t("workspace.filePane.viewer.notFoundNamed", { path })
      : t("workspace.filePane.viewer.notFound");
  }
  if (err.message?.includes("exceeds preview limit")) {
    return t("workspace.filePane.viewer.tooLarge");
  }
  // Deliberately the GENERIC key, never `err.message`. Raw messages are
  // dev-facing; they belong in the console, not in front of a lawyer.
  return t("workspace.filePane.viewer.errorGeneric");
}
